import { browser } from '$app/environment';
import { v4 as uuidv4 } from 'uuid';
import { LOCAL_STORAGE_KEYS, QUERY_PARAM_KEYS } from './constants';
import * as Sentry from '@sentry/sveltekit';

/**
 * Gets an existing device ID from localStorage.
 * This is a read-only operation - device ID should be initialized
 * during app startup via initializeDeviceId().
 * @returns {string | null} The existing device ID, or null if none exists
 */
export const getDeviceId = () => {
	if (!browser) return null;
	return localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
};

/**
 * Sets Sentry tags and user context for a device ID.
 * This should be called after the device ID is saved to localStorage.
 * @param {string} deviceId - The device ID to set in Sentry
 */
const setSentryDeviceId = (deviceId) => {
	Sentry.setTag('device_id', deviceId);
	Sentry.setUser({ id: deviceId });
};

/**
 * Creates a new device ID and saves it to localStorage.
 * Also sets Sentry tags and user context.
 * @returns {void}
 */
const createAndSaveDeviceId = () => {
	const deviceId = uuidv4();
	localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);
	setSentryDeviceId(deviceId);
};

/**
 * Saves a device ID from URL params to localStorage.
 * Also sets Sentry tags and user context after saving.
 * This is called when device_id is provided as a URL parameter.
 * @param {string} deviceId - The device ID from URL params
 */
const saveDeviceIdFromUrl = (deviceId) => {
	localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);
	setSentryDeviceId(deviceId);
};

/**
 * Initializes the device ID once per web app load.
 * This should be called early in the app lifecycle (e.g., in +layout.js).
 * Behavior:
 * - Checks URL params first (if found, saves it to localStorage)
 * - Checks localStorage for existing device ID
 * - Creates and saves a new device ID if none exists
 * Use getDeviceId() to read the device ID after initialization.
 * @returns {void}
 */
export const initializeDeviceId = () => {
	if (!browser) {
		return;
	}

	const searchParams = new URLSearchParams(window.location.search);
	if (searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID)) {
		const deviceIdFromUrl = searchParams.get(QUERY_PARAM_KEYS.DEVICE_ID) || '';
		if (deviceIdFromUrl) {
			saveDeviceIdFromUrl(deviceIdFromUrl);
			return;
		}
	}

	// Try to get existing device ID
	const existingDeviceId = getDeviceId();
	if (existingDeviceId) {
		// Set Sentry tags for existing device ID (in case context was lost on page reload)
		setSentryDeviceId(existingDeviceId);
		return;
	}

	// Create and save a new device ID
	createAndSaveDeviceId();
};
